/**
 * Approver Dashboard — Digi Sahyadri WRMS
 * For: Dept Head, Production Head, Manager, Director
 * Shows pending approvals at their stage with one-click approve/reject
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { Modal, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaCheck, FaTimes, FaFilter, FaSync, FaExternalLinkAlt, FaCalendarAlt, FaUserTie, FaInbox, FaHistory, FaSearch, FaThLarge, FaList, FaClock } from 'react-icons/fa';
import StatusBadge from '../shared/StatusBadge';
import RequestCard from '../shared/RequestCard';
import { getRequestsForApprover, approveStage, rejectRequest, getRequestTypeLabel, getRequestsHistoryForApprover } from '../../services/wrmsService';
import { isDeptVisibleToUser } from '../../../utils/departmentVisibility';
import { isReportingManagerOfRequest, isHandoverColleagueOfRequest } from '../../utils/ApprovalRouter';

const TYPE_FILTER = [
  { value: '', label: 'All Types' },
  { value: 'leave', label: 'Leave' },
  { value: 'early_leave', label: 'Early Leave' },
  { value: 'gate_pass', label: 'Gate Pass' },
  { value: 'extra_work', label: 'Extra Work' },
];

const ApproverDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState(location.state?.typeFilter || '');
  const [rejectModal, setRejectModal] = useState({ show: false, request: null });
  const [remarks, setRemarks] = useState('');
  const [processing, setProcessing] = useState(null);

  // Tab & History tracking states
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'history'
  const [historyRequests, setHistoryRequests] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState('');

  // New Filter & Layout States for pending inbox and date filters
  const [pendingSearch, setPendingSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [layoutMode, setLayoutMode] = useState('list');

  const fetchRequests = useCallback(async () => {
    const role = user?.role || 'employee';
    setLoading(true);
    try {
      const data = await getRequestsForApprover(role, user?.department, user);
      setRequests(data);
    } catch (err) {
      toast.error('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchHistory = useCallback(async () => {
    if (!user) return;
    setHistoryLoading(true);
    try {
      const data = await getRequestsHistoryForApprover(user);
      setHistoryRequests(data);
    } catch (err) {
      toast.error('Failed to load tracking history');
    } finally {
      setHistoryLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    } else {
      fetchRequests();
    }
  }, [activeTab, fetchRequests, fetchHistory]);

  const handleApprove = async (req) => {
    setProcessing(req.id);
    try {
      await approveStage(req.requestType, req.id, user, 'Approved');
      toast.success('Request approved!');
      fetchRequests();
      fetchHistory();
    } catch (err) {
      toast.error('Failed to approve request');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectConfirm = async () => {
    if (!remarks.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    const req = rejectModal.request;
    setProcessing(req.id);
    try {
      await rejectRequest(req.requestType, req.id, user, remarks);
      toast.success('Request rejected');
      setRejectModal({ show: false, request: null });
      setRemarks('');
      fetchRequests();
      fetchHistory();
    } catch (err) {
      toast.error('Failed to reject request');
    } finally {
      setProcessing(null);
    }
  };

  const filteredRequests = requests.filter(r => {
    // Reporting Manager and Handover Colleague must always see their direct requests
    const isDirect = isReportingManagerOfRequest(user, r) || isHandoverColleagueOfRequest(user, r);
    if (!isDirect && (user?.role === 'dept_head' || user?.role === 'production_head') && !isDeptVisibleToUser(r.department, r.department, user)) {
      return false;
    }

    const matchType = !typeFilter || r.requestType === typeFilter;
    const matchSearch = !pendingSearch ||
      r.employeeName?.toLowerCase().includes(pendingSearch.toLowerCase()) ||
      r.requestId?.toLowerCase().includes(pendingSearch.toLowerCase()) ||
      (r.reason && r.reason.toLowerCase().includes(pendingSearch.toLowerCase())) ||
      (r.description && r.description.toLowerCase().includes(pendingSearch.toLowerCase()));

    let matchDate = true;
    if (startDate || endDate) {
      const reqDate = r.submittedAt?.toDate ? r.submittedAt.toDate() : new Date(r.submittedAt);
      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        if (reqDate < start) matchDate = false;
      }
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (reqDate > end) matchDate = false;
      }
    }
    return matchType && matchSearch && matchDate;
  });

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getHoursAgo = (ts) => {
    if (!ts) return 0;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60));
  };

  const filteredHistory = historyRequests.filter(r => {
    // Reporting Manager and Handover Colleague must always see their direct requests
    const isDirect = isReportingManagerOfRequest(user, r) || isHandoverColleagueOfRequest(user, r);
    if (!isDirect && (user?.role === 'dept_head' || user?.role === 'production_head') && !isDeptVisibleToUser(r.department, r.department, user)) {
      return false;
    }

    const matchType = !typeFilter || r.requestType === typeFilter;
    const matchSearch = !historySearch ||
      r.employeeName?.toLowerCase().includes(historySearch.toLowerCase()) ||
      r.requestId?.toLowerCase().includes(historySearch.toLowerCase()) ||
      (r.reason && r.reason.toLowerCase().includes(historySearch.toLowerCase())) ||
      (r.description && r.description.toLowerCase().includes(historySearch.toLowerCase()));

    let matchDate = true;
    if (startDate || endDate) {
      const reqDate = r.submittedAt?.toDate ? r.submittedAt.toDate() : new Date(r.submittedAt);
      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        if (reqDate < start) matchDate = false;
      }
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (reqDate > end) matchDate = false;
      }
    }
    return matchType && matchSearch && matchDate;
  });

  return (
    <div style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
          <h5 style={{ fontWeight: 800, color: '#0f172a', margin: 0, fontSize: '1.15rem' }}>
            {activeTab === 'pending' ? 'Pending Approvals Inbox' : 'Track Requests & History'}
          </h5>
          <p style={{ color: '#64748b', fontSize: '0.82rem', margin: '4px 0 0' }}>
            {activeTab === 'pending'
              ? `Requests currently awaiting your approval as `
              : `View status, details, and history of all requests in your chain as `}
            <strong style={{ color: '#4f46e5' }}>{user?.role}</strong>
          </p>
        </div>
        <button className="wrms-btn-outline" onClick={activeTab === 'history' ? fetchHistory : fetchRequests} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <FaSync size={12} /> Refresh
        </button>
      </div>

      {/* Tab Switcher */}
      <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: '20px', gap: '16px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <button
          onClick={() => setActiveTab('pending')}
          style={{
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'pending' ? '3px solid #4f46e5' : '3px solid transparent',
            color: activeTab === 'pending' ? '#4f46e5' : '#64748b',
            padding: '10px 16px 8px',
            fontWeight: 700,
            fontSize: '0.88rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s',
          }}
        >
          <FaInbox size={14} /> Pending Inbox
          {requests.length > 0 && (
            <span style={{
              background: '#ef4444',
              color: 'white',
              fontSize: '0.7rem',
              padding: '2px 6px',
              borderRadius: '10px',
              fontWeight: 800
            }}>
              {requests.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('history')}
          style={{
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'history' ? '3px solid #4f46e5' : '3px solid transparent',
            color: activeTab === 'history' ? '#4f46e5' : '#64748b',
            padding: '10px 16px 8px',
            fontWeight: 700,
            fontSize: '0.88rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s',
          }}
        >
          <FaHistory size={14} /> Track Status / History
        </button>
      </div>

      {activeTab === 'pending' ? (
        <>
          {/* Count badge */}
          <div style={{ marginBottom: '20px' }}>
            <span style={{
              background: filteredRequests.length > 0 ? '#fef2f2' : '#ecfdf5',
              color: filteredRequests.length > 0 ? '#dc2626' : '#059669',
              border: `1px solid ${filteredRequests.length > 0 ? '#fca5a5' : '#a7f3d0'}`,
              padding: '4px 14px',
              borderRadius: '20px',
              fontSize: '0.78rem',
              fontWeight: 700,
            }}>
              {filteredRequests.length} pending {filteredRequests.length === 1 ? 'request' : 'requests'}
            </span>
          </div>

          {/* Filter Bar */}
          <div className="wrms-emp-filter-bar">
            {/* Search & Dates */}
            <div className="wrms-emp-filter-left">
              {/* Search input */}
              <div className="wrms-admin-search-wrap" style={{ flex: 1, minWidth: '180px' }}>
                <FaSearch className="wrms-search-icon" />
                <input
                  className="wrms-form-control form-control"
                  placeholder="Search by name or ID..."
                  value={pendingSearch}
                  onChange={e => setPendingSearch(e.target.value)}
                  style={{ fontSize: '0.82rem', height: '36px' }}
                />
              </div>
              {/* Start & End Dates */}
              <div className="wrms-emp-filter-dates">
                <div className="wrms-date-field" style={{ flex: 1 }}>
                  <span className="wrms-date-label">From</span>
                  <input
                    type="date"
                    className="wrms-form-control form-control"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    style={{ fontSize: '0.82rem', height: '36px', padding: '6px 10px' }}
                  />
                </div>
                <div className="wrms-date-field" style={{ flex: 1 }}>
                  <span className="wrms-date-label">To</span>
                  <input
                    type="date"
                    className="wrms-form-control form-control"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    style={{ fontSize: '0.82rem', height: '36px', padding: '6px 10px' }}
                  />
                </div>
              </div>
              {/* Clear button */}
              {(pendingSearch || startDate || endDate) && (
                <button
                  className="btn btn-link btn-sm p-0 text-decoration-none"
                  onClick={() => {
                    setPendingSearch('');
                    setStartDate('');
                    setEndDate('');
                  }}
                  style={{ fontSize: '0.75rem', fontWeight: 700, color: '#ef4444' }}
                >
                  Clear Filters
                </button>
              )}
            </div>

            {/* Grid / List Toggles */}
            <div className="wrms-view-toggle">
              <button
                className={layoutMode === 'list' ? 'active' : ''}
                onClick={() => setLayoutMode('list')}
                title="List View"
              >
                <FaList size={13} />
              </button>
              <button
                className={layoutMode === 'grid' ? 'active' : ''}
                onClick={() => setLayoutMode('grid')}
                title="Grid View"
              >
                <FaThLarge size={13} />
              </button>
            </div>
          </div>

          {/* Type filter */}
          <div className="wrms-filter-bar mb-3 d-flex align-items-center gap-2">
            <FaFilter size={13} style={{ color: '#64748b', flexShrink: 0 }} />
            <div className="wrms-type-filter-pills">
              {TYPE_FILTER.map(f => (
                <button
                  key={f.value}
                  onClick={() => setTypeFilter(f.value)}
                  style={{
                    padding: '5px 14px',
                    borderRadius: '20px',
                    border: '1.5px solid',
                    borderColor: typeFilter === f.value ? '#4f46e5' : '#e2e8f0',
                    background: typeFilter === f.value ? '#eef2ff' : 'white',
                    color: typeFilter === f.value ? '#4f46e5' : '#64748b',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Requests List */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
              <div className="spinner-border text-primary" role="status" />
              <p style={{ marginTop: '16px' }}>Loading pending approvals...</p>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="wrms-empty-state">
              <div className="wrms-empty-state-icon"><FaCheck size={36} color="#059669" /></div>
              <h5>All Clear!</h5>
              <p>No pending approvals match your criteria.</p>
            </div>
          ) : layoutMode === 'grid' ? (
            <div className="wrms-request-grid">
              {filteredRequests.map(req => (
                <RequestCard
                  key={req.id}
                  request={req}
                  showEmployee={true}
                  layout="grid"
                  approverActions={{
                    handleApprove,
                    handleReject: (r) => setRejectModal({ show: true, request: r })
                  }}
                />
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {filteredRequests.map(req => (
                <RequestCard
                  key={req.id}
                  request={req}
                  showEmployee={true}
                  layout="list"
                  approverActions={{
                    handleApprove,
                    handleReject: (r) => setRejectModal({ show: true, request: r })
                  }}
                />
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          {/* History Search & Filters */}
          <div className="wrms-emp-filter-bar">
            {/* Search & Dates */}
            <div className="wrms-emp-filter-left">
              {/* Search input */}
              <div className="wrms-admin-search-wrap" style={{ flex: 1, minWidth: '180px' }}>
                <FaSearch className="wrms-search-icon" />
                <input
                  className="wrms-form-control form-control"
                  placeholder="Search history by name or ID..."
                  value={historySearch}
                  onChange={e => setHistorySearch(e.target.value)}
                  style={{ fontSize: '0.82rem', height: '36px' }}
                />
              </div>
              {/* Start & End Dates */}
              <div className="wrms-emp-filter-dates">
                <div className="wrms-date-field" style={{ flex: 1 }}>
                  <span className="wrms-date-label">From</span>
                  <input
                    type="date"
                    className="wrms-form-control form-control"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    style={{ fontSize: '0.82rem', height: '36px', padding: '6px 10px' }}
                  />
                </div>
                <div className="wrms-date-field" style={{ flex: 1 }}>
                  <span className="wrms-date-label">To</span>
                  <input
                    type="date"
                    className="wrms-form-control form-control"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    style={{ fontSize: '0.82rem', height: '36px', padding: '6px 10px' }}
                  />
                </div>
              </div>
              {/* Clear button */}
              {(historySearch || startDate || endDate) && (
                <button
                  className="btn btn-link btn-sm p-0 text-decoration-none"
                  onClick={() => {
                    setHistorySearch('');
                    setStartDate('');
                    setEndDate('');
                  }}
                  style={{ fontSize: '0.75rem', fontWeight: 700, color: '#ef4444' }}
                >
                  Clear Filters
                </button>
              )}
            </div>

            {/* Grid / List Toggles */}
            <div className="wrms-view-toggle">
              <button
                className={layoutMode === 'list' ? 'active' : ''}
                onClick={() => setLayoutMode('list')}
                title="List View"
              >
                <FaList size={13} />
              </button>
              <button
                className={layoutMode === 'grid' ? 'active' : ''}
                onClick={() => setLayoutMode('grid')}
                title="Grid View"
              >
                <FaThLarge size={13} />
              </button>
            </div>
          </div>

          <div className="wrms-filter-bar mb-3 d-flex align-items-center gap-2">
            <div className="wrms-type-filter-pills">
              {TYPE_FILTER.map(f => (
                <button
                  key={f.value}
                  onClick={() => setTypeFilter(f.value)}
                  style={{
                    padding: '5px 14px',
                    borderRadius: '20px',
                    border: '1.5px solid',
                    borderColor: typeFilter === f.value ? '#4f46e5' : '#e2e8f0',
                    background: typeFilter === f.value ? '#eef2ff' : 'white',
                    color: typeFilter === f.value ? '#4f46e5' : '#64748b',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* History Requests List */}
          {historyLoading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
              <div className="spinner-border text-primary" role="status" />
              <p style={{ marginTop: '16px' }}>Loading tracking history...</p>
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="wrms-empty-state">
              <div className="wrms-empty-state-icon"><FaSearch size={36} color="#94a3b8" /></div>
              <h5>No Requests Found</h5>
              <p>We couldn't find any request logs matching your filters.</p>
            </div>
          ) : layoutMode === 'grid' ? (
            <div className="wrms-request-grid">
              {filteredHistory.map(req => (
                <RequestCard key={req.id} request={req} showEmployee={true} layout="grid" />
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {filteredHistory.map(req => (
                <RequestCard key={req.id} request={req} showEmployee={true} layout="list" />
              ))}
            </div>
          )}
        </>
      )}

      {/* Reject Reason Modal */}
      <Modal
        show={rejectModal.show}
        onHide={() => setRejectModal({ show: false, request: null })}
        centered
        className="wrms-approve-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a' }}>
            Reject Request
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '24px' }}>
          {rejectModal.request && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px' }}>
              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#dc2626' }}>
                {rejectModal.request.employeeName} — {getRequestTypeLabel(rejectModal.request.requestType)}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>
                {rejectModal.request.requestId}
              </div>
            </div>
          )}
          <Form.Group>
            <Form.Label style={{ fontWeight: 700, fontSize: '0.82rem', color: '#0f172a' }}>
              Rejection Reason <span style={{ color: '#dc2626' }}>*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Please provide a clear reason for rejection. The employee will be notified."
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
              style={{ borderRadius: '10px', fontSize: '0.875rem' }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #f1f5f9', padding: '16px 24px' }}>
          <button className="wrms-btn-outline" onClick={() => setRejectModal({ show: false, request: null })}
            style={{ padding: '8px 20px' }}>
            Cancel
          </button>
          <button className="wrms-btn-danger"
            onClick={handleRejectConfirm}
            disabled={processing !== null}
            style={{ padding: '8px 20px', background: '#dc2626', color: 'white', border: 'none' }}>
            {processing ? 'Rejecting...' : 'Confirm Rejection'}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ApproverDashboard;
